import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getTryOnImageUrl, postTryOnGenerate } from '../../api/tryon';
import { createTryonPayment, fetchTryonUnitPrice } from '../../api/financeTryon';
import Button from '../../components/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useFittingDraft } from '../../contexts/FittingDraftContext';
import { TRYON_LAST_USER_PHOTO_URL_KEY, TRYON_PICK_STORAGE_KEY } from '../../constants/tryonPickStorage';
import { absolutePhotoUrl, canonicalTryonType } from './tryonCatalog';
import {
  consumeTryonGenerationResult,
  startTryonGeneration,
  subscribeTryonGeneration,
} from '../../utils/tryonGenerationManager';
import { openYookassaConfirmationUrl } from '../../utils/openYookassaPayment';
import { startTryonPaymentPolling } from '../../utils/paymentReturnPolling';
import { normalizeImageFileForUpload } from '../../utils/normalizeImageFileForUpload';
import { formatRublesForUser, formatRublesPlain } from '../../utils/formatRubles';
import './FittingPage.css';

function isTryonEnabled() {
  const v = (process.env.REACT_APP_TRYON_ENABLE || '').trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

/** Шуточные статусы при генерации */
const FUNNY_LOADING_LINES = [
  'Ищем одежду на складе…',
  'Пытаемся вас переодеть…',
  'Согласовываем фасон с манекеном…',
  'Подбираем нитки в тон настроению…',
  'Пересчитываем пуговицы…',
  'Убеждаем нейросеть, что это именно вы…',
  'Чуть-чуть магии и много математики…',
  'Почти готово — осталось невозможное…',
  'Звоним на подмогу стилистам из 2160-го…',
  'Кот модель сбежал — ловим обратно…',
  'Согласуем оттенок с системной темой iOS…',
  'Промты крутим тоньше, чем настройки Privacy…',
  'Это не баг, это «ещё секунда гению»…',
  'Если бы это шили вручную — вы уже в тренде…',
  'Рендерим атмосферу и пару лишних пикселей шарма…',
  'Дресс-код склада: почти смарт-кэжуал…',
  'Шьём цифровую нить судьбы…',
  'Один момент — нейросеть надевает галстук…',
  'Ткань битая не битая — только loss приличный…',
  'Калибруем силуэт под ваш лучший ракурс…',
];

function itemThumbSrc(item) {
  const path = item?.photos?.[0]?.file_path;
  return path ? `/${String(path).replace(/^\/+/, '')}` : '';
}

/** Та же иконка корзины, что в CartItemCard (`cart-card__delete`). */
const SVG_TRASH = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor" />
  </svg>
);

function GarmentStackSlot({ label, item, onPick, onClear }) {
  const src = itemThumbSrc(item);
  const hasItem = Boolean(item);
  return (
    <div className="fitting-garment-slot">
      <button
        type="button"
        className="fitting-garment-slot--btn"
        onClick={onPick}
        aria-label={`Выбрать ${label.toLowerCase()} из избранного`}
      >
        {src ? (
          <img src={src} alt="" className="fitting-garment-slot__img" />
        ) : (
          <div className="fitting-garment-slot__placeholder" aria-hidden="true" />
        )}
        <span className="fitting-garment-slot__label">{label}</span>
      </button>
      {hasItem && onClear ? (
        <button
          type="button"
          className="fitting-garment-slot__clear"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClear();
          }}
          aria-label={`Убрать ${label.toLowerCase()} из примерки`}
        >
          {SVG_TRASH}
        </button>
      ) : null}
    </div>
  );
}

export default function FittingPage() {
  if (!isTryonEnabled()) return <FittingPageStub />;
  return <FittingPageActive />;
}

function FittingPageStub() {
  return (
    <div className="fitting-page fitting-page--stub">
      <div className="fitting-page__content">
        <div className="fitting-page__visual" aria-hidden="true">
          <div className="fitting-page__figure" />
          <div className="fitting-page__layers">
            <span className="fitting-page__layer fitting-page__layer--1" />
            <span className="fitting-page__layer fitting-page__layer--2" />
            <span className="fitting-page__layer fitting-page__layer--3" />
          </div>
        </div>
        <h1 className="fitting-page__title">Примерка</h1>
        <p className="fitting-page__badge">Скоро</p>
        <p className="fitting-page__lead">
          Собирай образы из вещей каталога и примеряй их на себя — без похода в примерочную.
        </p>
        <ul className="fitting-page__features">
          <li>
            <strong>Конструктор образов</strong> — собери лук из вещей в одном месте: верх, низ, обувь.
          </li>
          <li>
            <strong>Примерка на себя</strong> — загрузи фото и увидишь, как образ будет смотреться на тебе.
          </li>
        </ul>
      </div>
    </div>
  );
}

function FittingPageActive() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const tryonCredits = user?.tryon_credits ?? 0;
  /** Без телефона в профиле оплата примерок на бэке недоступна. */
  const hasPhoneForTryonPayment = useMemo(
    () => Boolean((user?.phone_local || '').trim()),
    [user?.phone_local],
  );

  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [buyQty, setBuyQty] = useState(1);
  const [unitPriceRub, setUnitPriceRub] = useState(null);
  const [buyLoading, setBuyLoading] = useState(false);
  const [buyAwaitingRedirect, setBuyAwaitingRedirect] = useState(false);
  const [buyErr, setBuyErr] = useState(null);

  useEffect(() => {
    refreshUser?.();
  }, [refreshUser]);

  useEffect(() => {
    return () => {
      tryonPollStopRef.current?.();
      tryonPollStopRef.current = null;
    };
  }, []);

  const {
    selUpper,
    setSelUpper,
    selLower,
    setSelLower,
    selShoe,
    setSelShoe,
    userFile,
    userPreviewUrl,
    setUserPhoto,
    resultUrl,
    setResultUrl,
    error,
    setError,
    loading,
    setLoading,
  } = useFittingDraft();

  const [funnyLineIdx, setFunnyLineIdx] = useState(0);
  const [pendingResultUrl, setPendingResultUrl] = useState(null);
  const [resultRevealMagic, setResultRevealMagic] = useState(false);
  const [photoHydrating, setPhotoHydrating] = useState(false);
  const pendingResultRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastPhotoHydrateDoneRef = useRef(false);
  /** true — ушли на оплату; не сбрасывать buyLoading, пока браузер не сменит страницу */
  const tryonPayRedirectRef = useRef(false);
  const tryonPollStopRef = useRef(null);

  const displayImageUrl = resultUrl || userPreviewUrl;

  useEffect(() => {
    let pick = location.state?.tryonPick;
    if (!pick?.slot || !pick?.item) {
      try {
        const raw = sessionStorage.getItem(TRYON_PICK_STORAGE_KEY);
        if (raw) {
          pick = JSON.parse(raw);
        }
      } catch (_) {
        sessionStorage.removeItem(TRYON_PICK_STORAGE_KEY);
        return;
      }
    }
    if (!pick?.slot || !pick?.item) return;

    try {
      sessionStorage.removeItem(TRYON_PICK_STORAGE_KEY);
    } catch (_) {}

    const item = { ...pick.item };
    const canon = canonicalTryonType(item.item_type);
    const nextState = { ...(location.state || {}) };
    delete nextState.tryonPick;
    const hasKeys = Object.keys(nextState).length > 0;
    if (!canon) {
      navigate(`${location.pathname}${location.search || ''}`, {
        replace: true,
        state: hasKeys ? nextState : {},
      });
      return;
    }
    item._tryonType = canon;

    if (pick.slot === 'upper') setSelUpper(item);
    else if (pick.slot === 'lower') setSelLower(item);
    else if (pick.slot === 'shoes') setSelShoe(item);

    navigate(`${location.pathname}${location.search || ''}`, {
      replace: true,
      state: hasKeys ? nextState : {},
    });
  }, [location.state, location.pathname, location.search, navigate]);

  /** Подставляем последнее фото с сервера (URL из прошлой генерации), чтобы не искать в галерее каждый раз. */
  useEffect(() => {
    if (lastPhotoHydrateDoneRef.current || userFile) {
      lastPhotoHydrateDoneRef.current = true;
      return undefined;
    }
    let url = null;
    try {
      url = localStorage.getItem(TRYON_LAST_USER_PHOTO_URL_KEY);
    } catch (_) {
      lastPhotoHydrateDoneRef.current = true;
      return undefined;
    }
    if (!url || typeof url !== 'string') {
      lastPhotoHydrateDoneRef.current = true;
      return undefined;
    }

    const ac = new AbortController();
    setPhotoHydrating(true);
    (async () => {
      try {
        const res = await fetch(url, { mode: 'cors', signal: ac.signal });
        if (!res.ok) throw new Error('fetch');
        const blob = await res.blob();
        if (!/^image\//.test(blob.type) && blob.size > 0) {
          // сервер мог отдать application/octet-stream — всё равно пробуем как картинку
        }
        const rawFile = new File([blob], 'last-tryon-photo.jpg', {
          type: blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg',
        });
        const file = await normalizeImageFileForUpload(rawFile);
        setUserPhoto(file);
        setResultUrl(null);
      } catch (_) {
        try {
          localStorage.removeItem(TRYON_LAST_USER_PHOTO_URL_KEY);
        } catch (e) {
          /* ignore */
        }
      } finally {
        if (!ac.signal.aborted) {
          setPhotoHydrating(false);
          lastPhotoHydrateDoneRef.current = true;
        }
      }
    })();

    return () => {
      ac.abort();
    };
  }, [setUserPhoto, setResultUrl, userFile]);

  useEffect(() => {
    if (!loading) return undefined;
    setFunnyLineIdx(0);
    const t = setInterval(() => {
      setFunnyLineIdx((i) => (i + 1) % FUNNY_LOADING_LINES.length);
    }, 3200);
    return () => clearInterval(t);
  }, [loading]);

  const goPickLiked = (slot) => {
    navigate(`/main/profile/liked?tryon=${encodeURIComponent(slot)}`);
  };

  const onPickUserPhoto = useCallback(
    async (e) => {
      const f = e.target.files?.[0];
      if (!f || !/^image\//.test(f.type)) return;
      const normalized = await normalizeImageFileForUpload(f);
      setUserPhoto(normalized);
      setResultUrl(null);
      setError(null);
      e.target.value = '';
    },
    [setUserPhoto, setResultUrl, setError],
  );

  const buildItemsPayload = useMemo(() => {
    const items = [];
    if (selUpper) {
      const url = absolutePhotoUrl(selUpper);
      const t = selUpper._tryonType || canonicalTryonType(selUpper.item_type);
      if (url && t) items.push({ image_url: url, type: t, description: selUpper.description || '' });
    }
    if (selLower) {
      const url = absolutePhotoUrl(selLower);
      const t = selLower._tryonType || canonicalTryonType(selLower.item_type);
      if (url && t) items.push({ image_url: url, type: t, description: selLower.description || '' });
    }
    if (selShoe) {
      const url = absolutePhotoUrl(selShoe);
      const t = selShoe._tryonType || canonicalTryonType(selShoe.item_type);
      if (url && t) items.push({ image_url: url, type: t, description: selShoe.description || '' });
    }
    return items;
  }, [selUpper, selLower, selShoe]);

  const canGenerate = userFile && buildItemsPayload.length > 0;

  const onPendingResultLoaded = useCallback(() => {
    const url = pendingResultRef.current;
    if (!url) return;
    pendingResultRef.current = null;
    setPendingResultUrl(null);
    setResultUrl(url);
    setLoading(false);
    setResultRevealMagic(true);
  }, [setLoading, setResultUrl]);

  const onPendingResultError = useCallback(() => {
    pendingResultRef.current = null;
    setPendingResultUrl(null);
    setLoading(false);
    setError('Не удалось загрузить результат — попробуй ещё раз');
  }, [setLoading, setError]);

  const openBuyModal = useCallback(async () => {
    if (!hasPhoneForTryonPayment) {
      const msg = 'Чтобы оплатить примерки, добавьте номер телефона в профиль.';
      if (window.Telegram?.WebApp?.showAlert) {
        window.Telegram.WebApp.showAlert(msg);
      } else {
        window.alert(msg);
      }
      return;
    }
    tryonPayRedirectRef.current = false;
    setBuyAwaitingRedirect(false);
    setUnitPriceRub(null);
    setBuyErr(null);
    setBuyModalOpen(true);
    setBuyLoading(true);
    try {
      const data = await fetchTryonUnitPrice();
      setUnitPriceRub(data.tryon_unit_price_rub != null ? Number(data.tryon_unit_price_rub) : null);
    } catch (e) {
      setBuyErr(e.message || 'Не удалось загрузить цену');
      setUnitPriceRub(null);
    } finally {
      setBuyLoading(false);
    }
  }, [hasPhoneForTryonPayment]);

  const closeBuyModal = useCallback(() => {
    if (tryonPayRedirectRef.current || buyAwaitingRedirect) return;
    // Запрос создания платежа — не закрываем, чтобы не дергать оплату параллельно
    if (buyLoading && unitPriceRub != null) return;
    tryonPayRedirectRef.current = false;
    setBuyAwaitingRedirect(false);
    setBuyModalOpen(false);
    setBuyErr(null);
    setBuyLoading(false);
  }, [buyAwaitingRedirect, buyLoading, unitPriceRub]);

  const onPayTryon = async () => {
    if (buyLoading || buyAwaitingRedirect) return;
    if (!hasPhoneForTryonPayment) {
      setBuyErr('Добавьте номер телефона в профиль, чтобы оплатить примерки.');
      return;
    }
    if (!unitPriceRub || unitPriceRub <= 0) {
      setBuyErr('Цена примерки не настроена');
      return;
    }
    const q = Math.max(1, Math.min(100, Number(buyQty) || 1));
    const origin = window.location.origin;
    const returnUrl = `${origin}/#/main/fitting`;
    setBuyLoading(true);
    setBuyErr(null);
    try {
      const data = await createTryonPayment({ quantity: q, returnUrl });
      if (data.owner_waiver) {
        refreshUser?.();
        tryonPayRedirectRef.current = false;
        setBuyAwaitingRedirect(false);
        setBuyLoading(false);
        setBuyModalOpen(false);
        setBuyErr(null);
        return;
      }
      const url = data.confirmation_url;
      if (!url) {
        setBuyErr('Нет ссылки на оплату');
        return;
      }
      tryonPayRedirectRef.current = true;
      setBuyAwaitingRedirect(true);
      tryonPollStopRef.current?.();
      tryonPollStopRef.current = startTryonPaymentPolling({
        paymentId: data.id,
        onCreditsReady: () => {
          tryonPollStopRef.current = null;
          refreshUser?.();
          tryonPayRedirectRef.current = false;
          setBuyAwaitingRedirect(false);
          setBuyLoading(false);
          setBuyModalOpen(false);
          setBuyErr(null);
        },
        onCanceled: () => {
          tryonPollStopRef.current = null;
          tryonPayRedirectRef.current = false;
          setBuyAwaitingRedirect(false);
          setBuyLoading(false);
          setBuyErr('Оплата отменена');
        },
        onTimeout: () => {
          tryonPollStopRef.current = null;
          tryonPayRedirectRef.current = false;
          setBuyAwaitingRedirect(false);
          setBuyLoading(false);
          refreshUser?.();
        },
      });
      openYookassaConfirmationUrl(url);
      // Не вызываем setBuyLoading(false) — иначе кнопка снова активна до ухода со страницы
    } catch (e) {
      setBuyErr(e.message || 'Ошибка оплаты');
    } finally {
      if (!tryonPayRedirectRef.current) {
        setBuyLoading(false);
        setBuyAwaitingRedirect(false);
      }
    }
  };

  const onGenerate = async () => {
    if (!canGenerate) return;
    setError(null);
    setResultUrl(null);
    setPendingResultUrl(null);
    pendingResultRef.current = null;
    setResultRevealMagic(false);
    try {
      await startTryonGeneration(() => postTryOnGenerate(userFile, buildItemsPayload));
    } catch (_) {
      // Ошибка уже прокинута в подписчиков менеджера
    }
  };

  useEffect(() => {
    const unsubscribe = subscribeTryonGeneration((snapshot) => {
      if (snapshot.status === 'running') {
        setLoading(true);
        return;
      }

      if (snapshot.status === 'error') {
        setLoading(false);
        setError(snapshot.error?.message || 'Ошибка генерации');
        return;
      }

      if (snapshot.status === 'success') {
        const data = consumeTryonGenerationResult() || snapshot.data;
        if (data?.user_photo_url) {
          try {
            localStorage.setItem(TRYON_LAST_USER_PHOTO_URL_KEY, data.user_photo_url);
          } catch (_) {
            /* ignore */
          }
        }
        const previewUrl =
          data?.result_id != null && String(data.result_id).trim()
            ? getTryOnImageUrl(data.result_id)
            : data?.result_url || null;
        if (previewUrl) {
          pendingResultRef.current = previewUrl;
          setPendingResultUrl(previewUrl);
          refreshUser?.();
        } else {
          setLoading(false);
          setError('Нет ссылки на результат');
        }
        return;
      }

      if (snapshot.status === 'idle') {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [refreshUser, setError, setLoading]);

  const onResultMagicEnd = useCallback((e) => {
    if (e.animationName !== 'fitting-magic-in') return;
    setResultRevealMagic(false);
  }, []);

  const openUserPhotoPicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="fitting-page">
      <div className="fitting-page__inner">
        <div className="fitting-page__gallery-banner">
          <div className="fitting-page__gallery-banner-row">
            <Link to="/main/fitting/gallery" className="fitting-page__gallery-link">
              Галерея примерок
            </Link>
            <button
              type="button"
              className="fitting-page__gallery-available fitting-page__gallery-available--btn"
              onClick={openBuyModal}
              disabled={!hasPhoneForTryonPayment}
              title={hasPhoneForTryonPayment ? undefined : 'Сначала добавьте номер телефона в профиль'}
            >
              Доступно примерок: {tryonCredits}
            </button>
          </div>
          {!hasPhoneForTryonPayment ? (
            <p className="fitting-page__phone-hint" role="status">
              Чтобы оплатить примерки,{' '}
              <Link to="/main/profile" className="fitting-page__phone-hint-link">
                добавьте номер в профиль.
              </Link>
            </p>
          ) : null}
          <div className="fitting-page__gallery-divider" aria-hidden="true" />
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="fitting-hidden-file"
          onChange={onPickUserPhoto}
          aria-label="Загрузить фото для примерки"
        />

        <div className="fitting-layout">
          <div className="fitting-layout__garments">
            <GarmentStackSlot
              label="Верх"
              item={selUpper}
              onPick={() => goPickLiked('upper')}
              onClear={() => setSelUpper(null)}
            />
            <GarmentStackSlot
              label="Низ"
              item={selLower}
              onPick={() => goPickLiked('lower')}
              onClear={() => setSelLower(null)}
            />
            <GarmentStackSlot
              label="Обувь"
              item={selShoe}
              onPick={() => goPickLiked('shoes')}
              onClear={() => setSelShoe(null)}
            />
          </div>
          <div className="fitting-layout__main">
            <div
              className={`fitting-user-frame ${loading ? 'fitting-user-frame--loading' : ''} ${displayImageUrl || photoHydrating ? 'fitting-user-frame--filled' : ''} ${displayImageUrl && !loading && !photoHydrating ? 'fitting-user-frame--tappable' : ''}`}
              role={displayImageUrl && !loading && !photoHydrating ? 'button' : undefined}
              tabIndex={displayImageUrl && !loading && !photoHydrating ? 0 : undefined}
              onClick={displayImageUrl && !loading && !photoHydrating ? openUserPhotoPicker : undefined}
              onKeyDown={
                displayImageUrl && !loading && !photoHydrating
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openUserPhotoPicker();
                      }
                    }
                  : undefined
              }
            >
              {pendingResultUrl ? (
                <img
                  src={pendingResultUrl}
                  alt=""
                  className="fitting-preload-img"
                  aria-hidden
                  onLoad={onPendingResultLoaded}
                  onError={onPendingResultError}
                />
              ) : null}
              {displayImageUrl || photoHydrating ? (
                <>
                  <div className="fitting-user-frame__stack">
                    {photoHydrating && !userPreviewUrl ? (
                      <div className="fitting-user-frame__hydrate" aria-live="polite">
                        Подгружаем последнее фото…
                      </div>
                    ) : null}
                    {userPreviewUrl ? (
                      <img
                        src={userPreviewUrl}
                        alt=""
                        className={`fitting-user-frame__base ${loading ? 'fitting-user-frame__img--spoiled' : ''}`}
                      />
                    ) : null}
                    {resultUrl ? (
                      <img
                        src={resultUrl}
                        alt=""
                        className={`fitting-user-frame__result ${resultRevealMagic ? 'fitting-user-frame__result--magic' : ''}`}
                        onAnimationEnd={onResultMagicEnd}
                      />
                    ) : null}
                  </div>
                  {loading && (
                    <div className="fitting-gen-apple" aria-hidden="true">
                      <div className="fitting-gen-apple__mesh" />
                      <div className="fitting-gen-apple__shimmer" />
                      <div className="fitting-gen-apple__frost" />
                      <div className="fitting-gen-apple__ring-wrap">
                        <div className="fitting-gen-apple__ring" />
                      </div>
                    </div>
                  )}
                  {loading && (
                    <p className="fitting-gen-apple__caption" role="status" aria-live="polite">
                      <span className="fitting-gen-apple__caption-inner" key={funnyLineIdx}>
                        {FUNNY_LOADING_LINES[funnyLineIdx]}
                      </span>
                    </p>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  className="fitting-user-frame__upload"
                  onClick={openUserPhotoPicker}
                >
                  Загрузить ваше фото
                </button>
              )}
            </div>
          </div>
        </div>

        <Button
          type="button"
          size="small"
          variant="primary"
          className="fitting-generate-btn"
          disabled={!canGenerate}
          loading={loading}
          onClick={onGenerate}
        >
          Сгенерировать примерку
        </Button>
        {!userFile && (
          <p className="fitting-page__muted">Нужно фото и хотя бы одна вещь из избранного</p>
        )}
        {userFile && buildItemsPayload.length === 0 && (
          <p className="fitting-page__muted">Выбери вещи: тап по слоту слева → избранное</p>
        )}

        {error && !loading && (
          <p className="fitting-page__banner fitting-page__banner--err">{error}</p>
        )}

        <p className="fitting-page__disclaimer" role="note">
          Результат AI-примерки не гарантирует полное совпадение с тем, как вещи будут сидеть на вас вживую.
          Используйте изображение только для визуального ориентира.
        </p>

        {buyModalOpen ? (
          <div
            className="fitting-buy-modal-backdrop"
            role="presentation"
            onClick={closeBuyModal}
          >
            <div
              className="fitting-buy-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Покупка примерок"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="fitting-buy-modal__header">
                <h2 className="fitting-buy-modal__title">Купить примерки</h2>
                <button
                  type="button"
                  className="fitting-buy-modal__close"
                  onClick={closeBuyModal}
                  aria-label="Закрыть"
                  disabled={
                    buyAwaitingRedirect ||
                    Boolean(buyLoading && unitPriceRub != null && unitPriceRub > 0)
                  }
                >
                  ×
                </button>
              </div>
              {buyLoading && !unitPriceRub && buyErr == null ? (
                <p className="fitting-buy-modal__muted">Загрузка…</p>
              ) : null}
              {buyErr ? <p className="fitting-buy-modal__err">{buyErr}</p> : null}
              {unitPriceRub != null && unitPriceRub > 0 ? (
                <>
                  <label className="fitting-buy-modal__label">
                    Количество
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={buyQty}
                      disabled={buyLoading || buyAwaitingRedirect}
                      onChange={(e) => setBuyQty(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                      className="fitting-buy-modal__input"
                    />
                  </label>
                  <p className="fitting-buy-modal__total">
                    Итого:{' '}
                    <strong>{formatRublesForUser(Number(unitPriceRub) * Number(buyQty))}</strong>
                    <span className="fitting-buy-modal__hint">
                      {' '}
                      ({formatRublesPlain(unitPriceRub)} ₽ × {buyQty})
                    </span>
                  </p>
                  <button
                    type="button"
                    className="fitting-buy-modal__pay"
                    disabled={buyLoading || buyAwaitingRedirect}
                    onClick={onPayTryon}
                  >
                    {buyAwaitingRedirect ? 'Переход к оплате…' : buyLoading ? 'Ожидание…' : 'Оплатить'}
                  </button>
                </>
              ) : !buyLoading && buyErr == null ? (
                <p className="fitting-buy-modal__muted">
                  Цена примерки в магазине не задана. Попросите администратора указать её в настройках финансов.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
